import { ResizablePanel } from "@cohesive/ui-components";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import {
  FLOATING_CHAT_INITIAL_WIDTH_PX,
  FLOATING_CHAT_MIN_WIDTH_PX,
  FLOATING_CHAT_MOTION_INITIAL,
  FLOATING_CHAT_MOTION_TRANSITION,
  FLOATING_WINDOW_PANEL_CLASS,
} from "./floatingChatShell";

/**
 * Windowed chat floating in the bottom-LEFT of the content region, over a
 * full-width-expanded detail pane (the normal corner is bottom-right; a
 * full-width pane occupies the right, so the window flips sides). Fixed
 * positioning because the in-flow region is squeezed to ~0 width. Shared by
 * the canvas and home/workspace panels.
 */
export function FloatingChatLeftWindow({
  anchorRect,
  viewportHeightPx,
  storageKey,
  maxWidthPx,
  initialHeightPx,
  minHeightPx,
  maxHeightPx,
  children,
}: {
  /** Content-region rect the window anchors to (`left`/`bottom`). */
  anchorRect: DOMRect;
  viewportHeightPx: number;
  /** Per-surface ResizablePanel width-persistence key. */
  storageKey: string;
  /** Viewport-based cap (the region's width is ~0 here, so callers pass e.g. 50% viewport). */
  maxWidthPx: number;
  initialHeightPx: number;
  minHeightPx: number;
  maxHeightPx: number;
  children: ReactNode;
}) {
  return (
    <AnimatePresence initial={false}>
      <motion.div
        key="left-window"
        data-floating-chat="left-window"
        className="fixed z-50 origin-bottom-left"
        style={{
          left: anchorRect.left + 8,
          bottom: viewportHeightPx - anchorRect.bottom + 8,
        }}
        initial={FLOATING_CHAT_MOTION_INITIAL}
        animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
        exit={FLOATING_CHAT_MOTION_INITIAL}
        transition={FLOATING_CHAT_MOTION_TRANSITION}
      >
        <ResizablePanel
          // Resizable floating window with the drag pills hidden
          // (`showHandleIndicator={false}`); `directions` drives both the
          // resize edges (right/top + corner) and its height management.
          directions={["right", "top", "top-right"]}
          showHandleIndicator={false}
          isClosed={false}
          transitionDuration={0}
          minWidth={FLOATING_CHAT_MIN_WIDTH_PX}
          maxWidth={maxWidthPx}
          initialWidth={FLOATING_CHAT_INITIAL_WIDTH_PX}
          initialHeight={initialHeightPx}
          minHeight={minHeightPx}
          maxHeight={maxHeightPx}
          storageKey={storageKey}
          className={FLOATING_WINDOW_PANEL_CLASS}
        >
          {children}
        </ResizablePanel>
      </motion.div>
    </AnimatePresence>
  );
}
